import { useEffect, useState, useCallback } from "react";
import { Loader2, ChevronLeft, ChevronRight, Plus, Trash2, X, Save, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { SummaryCards, CalendarGrid } from "../../components/employee/TimesheetCalendar";

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli",
  "August", "September", "Oktober", "November", "Dezember"];

const emptyEntry = { start: "08:00", end: "16:00", break_minutes: 30, note: "", kind: "work" };

const hhmm = (v) => {
  const n = Number(v || 0);
  const mins = Math.round(Math.abs(n) * 60);
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
};

export default function Timesheet() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayOpen, setDayOpen] = useState(null); // date string
  const [form, setForm] = useState(null); // {id?, start, end, break_minutes, note}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/timesheet", { params: { year, month } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const entriesByDate = {};
  (data?.entries || []).forEach((e) => {
    (entriesByDate[e.date] = entriesByDate[e.date] || []).push(e);
  });

  const prev = () => { if (month === 1) { setMonth(12); setYear((y) => y - 1); } else setMonth((m) => m - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear((y) => y + 1); } else setMonth((m) => m + 1); };

  const openDay = (dateStr) => { setDayOpen(dateStr); setForm(null); };
  const dayEntries = dayOpen ? (entriesByDate[dayOpen] || []) : [];

  const saveEntry = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = { date: dayOpen, start: form.start, end: form.end,
        break_minutes: Number(form.break_minutes) || 0, note: form.note || "",
        kind: form.kind || "work" };
      if (form.id) await api.patch(`/me/time-entries/${form.id}`, payload);
      else await api.post("/me/time-entries", payload);
      toast.success("Eintrag gespeichert.");
      setForm(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const delEntry = async (id) => {
    if (!window.confirm("Eintrag löschen?")) return;
    await api.delete(`/me/time-entries/${id}`);
    toast.success("Gelöscht.");
    await load();
  };

  const dfmt = (ds) => {
    const [y, m, d] = ds.split("-");
    return `${d}.${m}.${y}`;
  };

  const downloadPdf = async () => {
    try {
      // Cache-Buster (_ts) verhindert, dass Browser/CDN ein altes PDF liefern
      const res = await api.get("/me/timesheet.pdf", {
        params: { year, month, _ts: Date.now() }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `arbeitszeitnachweis-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF konnte nicht erstellt werden.");
    }
  };

  return (
    <div data-testid="employee-timesheet">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-anthracite md:text-3xl">Mein Stundennachweis</h1>
          <p className="mt-1 text-sm text-mid-gray">Tag im Kalender antippen und Arbeitszeit eintragen.</p>
        </div>
        <div className="flex items-center gap-2 rounded-sm border border-light-gray bg-white px-2 py-1.5">
          <button onClick={prev} className="rounded-sm p-1.5 hover:bg-light-gray" data-testid="ts-prev" aria-label="Vorheriger Monat"><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[140px] text-center font-heading text-sm font-bold text-anthracite" data-testid="ts-month">{MONTHS[month - 1]} {year}</span>
          <button onClick={next} className="rounded-sm p-1.5 hover:bg-light-gray" data-testid="ts-next" aria-label="Nächster Monat"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <button
          onClick={downloadPdf}
          className="inline-flex items-center gap-2 rounded-sm bg-anthracite px-4 py-2 font-heading text-sm font-bold text-white hover:bg-brand-black"
          data-testid="ts-download-pdf"
        >
          <FileDown className="h-4 w-4" /> PDF-Nachweis
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-yellow" /></div>
      ) : (
        <>
          <div className="mt-6"><SummaryCards s={data} /></div>
          <CalendarGrid year={year} month={month} entriesByDate={entriesByDate} onDayClick={openDay} />
        </>
      )}

      {dayOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-black/60 p-4" data-testid="day-editor" onClick={(e) => { if (e.target === e.currentTarget) { setDayOpen(null); setForm(null); } }}>
          <div className="my-8 w-full max-w-md rounded-md bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-anthracite">{dfmt(dayOpen)}</h2>
              <button onClick={() => { setDayOpen(null); setForm(null); }} data-testid="day-close"><X className="h-5 w-5 text-mid-gray" /></button>
            </div>

            {/* vorhandene Einträge */}
            <div className="mt-4 space-y-2">
              {dayEntries.length === 0 && !form && (
                <p className="rounded-sm bg-light-gray p-3 text-sm text-mid-gray" data-testid="day-empty">Noch keine Einträge für diesen Tag.</p>
              )}
              {dayEntries.map((e) => {
                const kind = e.kind || "work";
                const label = kind === "vacation" ? "Urlaub" : kind === "sick" ? "Krankheit" : `${e.start}–${e.end}`;
                return (
                  <div key={e.id} className="flex items-center justify-between rounded-sm border border-light-gray p-3" data-testid={`day-entry-${e.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-anthracite">{label} · {hhmm(e.hours)} h</p>
                      <p className="text-xs text-mid-gray">
                        {kind === "work" ? `Pause ${e.break_minutes} min` : (kind === "vacation" ? "Urlaubstag" : "Krankheitstag")}
                        {e.note ? ` · ${e.note}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setForm({ ...e })} className="text-xs font-bold text-anthracite hover:text-yellow-dark">Bearbeiten</button>
                      <button onClick={() => delEntry(e.id)} className="text-red-500" data-testid={`day-del-${e.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Formular */}
            {form ? (
              <div className="mt-4 space-y-3 rounded-sm bg-light-gray p-4">
                <label className="block text-sm font-semibold text-anthracite">Eintragsart
                  <select
                    value={form.kind || "work"}
                    onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
                    className="mt-1 w-full rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow"
                    data-testid="entry-kind"
                  >
                    <option value="work">Arbeitszeit</option>
                    <option value="vacation">Urlaub</option>
                    <option value="sick">Krankheit</option>
                  </select>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-sm font-semibold text-anthracite">Von<input type="time" value={form.start} onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="entry-start" inputMode="numeric" /></label>
                  <label className="text-sm font-semibold text-anthracite">Bis<input type="time" value={form.end} onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="entry-end" inputMode="numeric" /></label>
                </div>
                <label className="block text-sm font-semibold text-anthracite">Pause (Minuten)<input type="number" min="0" value={form.break_minutes} onChange={(e) => setForm((f) => ({ ...f, break_minutes: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="entry-break" inputMode="numeric" /></label>
                <label className="block text-sm font-semibold text-anthracite">Bemerkung / Tätigkeit / Einsatz (optional)<input value={form.note || ""} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="entry-note" /></label>
                {form.kind === "vacation" && (
                  <p className="rounded-sm bg-orange-50 p-2 text-xs text-anthracite">
                    Hinweis: Als Urlaubsstunden wird die Zeit zwischen Beginn und Ende angerechnet
                    (typischerweise Ihre reguläre Sollzeit an diesem Tag, z. B. 6:30 h).
                  </p>
                )}
                {form.kind === "sick" && (
                  <p className="rounded-sm bg-blue-50 p-2 text-xs text-anthracite">
                    Hinweis: Krankheitsstunden werden im Monatssaldo geführt und als „Krank“ markiert.
                  </p>
                )}
                <div className="flex gap-2">
                  <button onClick={saveEntry} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-yellow px-4 py-2.5 font-heading text-sm font-bold text-brand-black hover:bg-yellow-dark disabled:opacity-60" data-testid="entry-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
                  </button>
                  <button onClick={() => setForm(null)} className="rounded-sm border border-light-gray px-4 py-2.5 text-sm font-semibold text-mid-gray hover:bg-light-gray">Abbrechen</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setForm({ ...emptyEntry })} className="mt-4 inline-flex items-center gap-2 rounded-sm bg-anthracite px-4 py-2.5 font-heading text-sm font-bold text-white hover:bg-brand-black" data-testid="entry-add">
                <Plus className="h-4 w-4" /> Zeit hinzufügen
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
